import { database } from "../index";
import {
  ATTENDANCE_SCHEMA,
  BUSINESS_SCHEMA,
  JOURNAL_SCHEMA,
  STAFF_SCHEMA,
  USER_SCHEMA,
} from "../schema";
import { Q } from "@nozbe/watermelondb";
import { getStaffPayment } from "../../constants";
import { uuid } from "../../oscar-pos-core/constants";
import moment from "moment";
import { getJournalByID } from "./journal";

const attendanceModel = database.collections.get(ATTENDANCE_SCHEMA);

export const getAttendanceByMonth = async (start, end, staff_id) =>
  await attendanceModel
    .query(
      Q.where("attendance_at", Q.between(Number(start), Number(end))),
      Q.on(STAFF_SCHEMA, "id", staff_id)
    )
    .fetch();

export const getAttendanceCountByMonth = async (start, end, staff_id) =>
  await attendanceModel
    .query(
      Q.where("attendance_at", Q.between(Number(start), Number(end))),
      Q.on(STAFF_SCHEMA, "id", staff_id)
    )
    .fetchCount();

export const getAttendancesById = async (staff_id) =>
  await attendanceModel.query(Q.on(STAFF_SCHEMA, "id", staff_id)).fetch();

export const getAttendancesByJournal = async (journal_id) =>
  await attendanceModel.query(Q.on(JOURNAL_SCHEMA, "id", journal_id)).fetch();

export const getStaffAttendanceByDate = async (staff_id, date) =>
  await attendanceModel
    .query(
      Q.where("attendance_at", Number(date)),
      Q.on(STAFF_SCHEMA, "id", staff_id)
    )
    .fetch();

export const observeActiveAttendances = (id) =>
  attendanceModel
    .query(
      Q.where("business_id", id),
      Q.on(BUSINESS_SCHEMA, [Q.where("is_active", true)])
    )
    .observeWithColumns([
      "attendance_at",
      "last_updated_at",
      "payment",
      "status",
      "extra_mode",
      "extra_payment",
      "total_minutes",
      "wage",
    ]);

export const getAttendanceByBusiness = async (id) =>
  await attendanceModel.query(Q.on(BUSINESS_SCHEMA, "id", id)).fetch();

export const getAttendanceCount = async () =>
  await attendanceModel.query().fetchCount();

const WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const prepareInsertion = (staff, attendances) => {
  return attendances.map((attendance) => {
    try {
      return attendanceModel.prepareCreate((field) => {
        field._raw.id = attendance.id;
        field.staff.set(staff);
        field.business.set(staff.business);
        field.attendance_at = attendance.attendance_at;
        field.check_in_at = attendance.check_in_at;
        field.check_out_at = attendance.check_out_at;
        field.payment = attendance.payment;
        field.last_updated_at = attendance.last_updated_at;
        field.status = attendance.status;
      });
    } catch (e) {
    }
  });
};

export const bulkCreateAttendances = async (staff, attendances) => {
  await database.write(async () => {
    const allRecords = prepareInsertion(staff, attendances);
    await database.batch(...allRecords);
  });
};

export const bulkUpdateAttendance = async (attendances, journal_id) => {
  await database.write(async () => {
    const journal = await getJournalByID(journal_id);
    const allRecords = await prepareJournalUpdation(attendances, journal);
    await database.batch(...allRecords);
  });
};

const prepareJournalUpdation = async (attendances, journal) => {
  return await Promise.all(
    attendances.map(async (attendance) => {
      try {
        // const a = await attendanceModel.find(attendance.id);
        // let getJournal = await getJournalByID(journal_id)

        return attendance.prepareUpdate((att) => {
          att.journal.set(journal);
          att.last_updated_at = new Date().getTime();
        });
      } catch (err) {
      }
    })
  );
};

export const bulkUpdateAttendanceWithoutJournal = async (
  attendances,
  staff
) => {
  await database.write(async () => {
    const allRecords = await prepareUpdation(attendances, staff);
    await database.batch(...allRecords);
  });
};

const prepareUpdation = async (attendances, staff) => {
  return await Promise.all(
    attendances.map(async (attendance) => {
      try {
        const a = await attendanceModel.find(attendance.id);

        return a.prepareUpdate((att) => {
          let day = WEEK[moment(att.attendance_at).day()];
          att.payment = attendance.payment;
          att.last_updated_at = attendance.last_updated_at;
          // att.status =  attendance.status
          att.status = staff.workdays.includes(day)
            ? attendance.status
            : "Paid Holiday";
        });
      } catch (err) {
      }
    })
  );
};

export const bulkUpdateStaffAttendance = async (
  allStaff,
  action,
  date,
  business
) => {
  await database.write(async () => {
    const allRecords = await prepareStaffUpdation(
      allStaff,
      action,
      date,
      business
    );
    await database.batch(...allRecords);
  });
};

const prepareStaffUpdation = async (allStaff, action, date, business) => {
  return await Promise.all(
    allStaff.map(async (staff) => {
      let att_date = new Date(date);
      att_date.setHours(0, 0, 0, 0);
      let att_at = att_date.getTime();

      try {
        let payment = getStaffPayment(action, staff, date);
        let attendance = await getStaffAttendanceByDate(staff.id, att_at);

        if (attendance.length) {
          if (attendance[0].total_minutes) {
            payment = payment + attendance[0].extra_payment;
          }
          return attendance[0].prepareUpdate((att) => {
            let day = WEEK[moment(att.attendance_at).day()];
            att.payment = payment;
            att.last_updated_at = new Date().getTime();
            // att.status = (staff.salary_type == 'Hourly' || staff.salary_type == 'Daily') ? action :
            //                 staff.workdays.includes(day) ? action : 'Absent'
            att.status =
              staff.salary_type == "Hourly"
                ? action
                : staff.workdays.includes(day)
                  ? action
                  : "Paid Holiday";
          });
        } else {
          let day = WEEK[moment(att_at).day()];
          return attendanceModel.prepareCreate((att) => {
            att.staff.set(staff);
            att.business.set(business);
            att._raw.id = uuid();
            att.attendance_at = att_at;
            att.check_in_at = new Date().getTime();
            att.check_out_at = new Date().getTime();
            att.status =
              staff.salary_type == "Hourly"
                ? action
                : staff.workdays.includes(day)
                  ? action
                  : "Paid Holiday";
            att.payment = payment;
            att.last_updated_at = new Date().getTime();
          });
        }
      } catch (err) {
      }
    })
  );
};

export const bulkDeleteAttendances = async (allAttendances) => {
  await database.write(async () => {
    const allRecords = await prepareDeletion(allAttendances);
    await database.batch(...allRecords);
  });
};

const prepareDeletion = async (allAttendances) => {
  return await Promise.all(
    allAttendances.map(async (attendanceModel) => {
      try {
        return attendanceModel.prepareMarkAsDeleted();
      } catch (err) {
      }
    })
  );
};

export const bulkDeleteAttendanceByBusiness = async (business_id) => {
  await database.write(async () => {
    let attendance = await getAttendanceByBusiness(business_id);
    const allRecords = await prepareDeletion(attendance);
    await database.batch(...allRecords);
  });
};
